import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { logout } from "../lib/auth";

import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// 🔔 NEW: bell component that calls getFcmToken + /user/subscribe
import NotificationBell from "../components/NotificationBell";

/** Small utilities */
const copy = async (txt) => {
  try {
    await navigator.clipboard.writeText(txt);
  } catch {
    // ignore
  }
};

const fmtDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-IN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

const fmtDT = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("en-IN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const pill = (status) => {
  let color = "#6b7280";
  let bg = "#e5e7eb";
  let label = status;

  if (!status) {
    label = "-";
  } else {
    const s = String(status).toUpperCase();
    if (s === "COMPLETED") {
      color = "#166534";
      bg = "#bbf7d0";
      label = "Success";
    } else if (s === "FAILED") {
      color = "#b91c1c";
      bg = "#fecaca";
      label = "Failed";
    } else if (s === "PENDING_PAYMENT") {
      color = "#92400e";
      bg = "#fed7aa";
      label = "Pending";
    }
  }

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 8px",
        fontSize: 11,
        borderRadius: 999,
        background: bg,
        color,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: 0.4,
      }}
    >
      {label}
    </span>
  );
};

/** Side drawer for registration/payment details */
function DetailsDrawer({ open, id, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [refreshNote, setRefreshNote] = useState("");
  const nav = useNavigate(); // for Receipt button
  const handleOpenReceipt = () => {
    if (!id) return;
    const url = `/receipt/${id}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    if (!open || !id) return;
    let alive = true;

    (async () => {
      try {
        setErr("");
        setLoading(true);
        const { data } = await api.get(`/api/admin/registrations/${id}`);
        if (alive) setDetail(data);
      } catch (e) {
        if (alive)
          setErr(e?.response?.data?.message || "Failed to load details");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
      setDetail(null);
      setErr("");
      setRefreshNote("");
    };
  }, [open, id]);

  const handleRefreshRazorpay = async () => {
    if (!id) return;
    try {
      setRefreshing(true);
      setRefreshNote("");

      // 1) Hit refresh API (updates DB based on Razorpay)
      await api.post(`/api/admin/registrations/${id}/refresh-razorpay`);

      // 2) Re-fetch full details
      const { data } = await api.get(`/api/admin/registrations/${id}`);
      setDetail(data);

      setRefreshNote("Refreshed from Razorpay successfully.");
    } catch (e) {
      console.error("refresh razorpay error", e);
      setRefreshNote(
        e?.response?.data?.message || "Failed to refresh from Razorpay"
      );
    } finally {
      setRefreshing(false);
    }
  };

  const styles = {
    wrap: {
      position: "fixed",
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      background: open ? "rgba(15, 23, 42, 0.35)" : "transparent",
      pointerEvents: open ? "auto" : "none",
      transition: "background 150ms ease",
      zIndex: 50,
    },
    panel: {
      position: "absolute",
      top: 0,
      right: 0,
      width: "420px",
      maxWidth: "90vw",
      height: "100%",
      background: "#fff",
      borderLeft: "1px solid #e5e7eb",
      boxShadow: "-8px 0 30px rgba(15,23,42,0.15)",
      transform: open ? "translateX(0)" : "translateX(100%)",
      transition: "transform 180ms ease",
      display: "flex",
      flexDirection: "column",
    },
    header: {
      padding: "12px 16px",
      borderBottom: "1px solid #e5e7eb",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
    },
    body: {
      flex: 1,
      overflowY: "auto",
      padding: 16,
      fontSize: 13,
    },
    title: {
      fontSize: 16,
      fontWeight: 600,
    },
    label: {
      width: 120,
      fontWeight: 600,
      color: "#4b5563",
    },
    row: {
      display: "flex",
      fontSize: 13,
      padding: "3px 0",
    },
  };

  const StatusPill = (status) => pill(status);

  if (!open) return null;

  return (
    <div style={styles.wrap} onClick={onClose}>
      <div style={styles.panel} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <div style={styles.title}>
            Details {id ? `#${id}` : ""}
            {detail?.registration?.full_name
              ? ` – ${detail.registration.full_name}`
              : ""}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {id && (
              <button
                type="button"
                onClick={handleOpenReceipt}
                style={{
                  border: "none",
                  background:
                    "linear-gradient(135deg,#0f766e 0%,#22c55e 50%,#0ea5e9 100%)",
                  color: "#fff",
                  borderRadius: 999,
                  padding: "6px 12px",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                View Receipt
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              style={{
                border: "none",
                background: "#f9fafb",
                borderRadius: 999,
                padding: "4px 10px",
                cursor: "pointer",
                fontSize: 13,
              }}
            >
              Close
            </button>
          </div>
        </div>
        <div style={styles.body}>
          {loading && <div>Loading…</div>}
          {err && (
            <div style={{ color: "#b91c1c", marginBottom: 8 }}>{err}</div>
          )}
          {!loading && detail && (
            <>
              <div
                style={{
                  marginBottom: 12,
                  fontWeight: 600,
                  fontSize: 14,
                  borderBottom: "1px solid #e5e7eb",
                  paddingBottom: 4,
                }}
              >
                Registration
              </div>
              <div>
                <div style={styles.row}>
                  <div style={styles.label}>Name</div>
                  <div>{detail.registration.full_name || "-"}</div>
                </div>
                <div style={styles.row}>
                  <div style={styles.label}>Mobile</div>
                  <div>{detail.registration.mobile || "-"}</div>
                </div>
                <div style={styles.row}>
                  <div style={styles.label}>Email</div>
                  <div>{detail.registration.email || "-"}</div>
                </div>
                <div style={styles.row}>
                  <div style={styles.label}>Delegation Type</div>
                  <div>{detail.registration.course_name || "-"}</div>
                </div>
                <div style={styles.row}>
                  <div style={styles.label}>Referred By</div>
                  <div>{detail.registration.referrer_name || "-"}</div>
                </div>
                <div style={styles.row}>
                  <div style={styles.label}>Amount (₹)</div>
                  <div>{detail.registration.amount_rupees ?? "-"}</div>
                </div>
                <div style={styles.row}>
                  <div style={styles.label}>Method</div>
                  <div>{detail.registration.method || "-"}</div>
                </div>
                <div style={styles.row}>
                  <div style={styles.label}>Address</div>
                  <div>{detail.registration.address || "-"}</div>
                </div>
                <div style={styles.row}>
                  <div style={styles.label}>Reg Status</div>
                  <div>
                    {StatusPill(detail.registration.registration_status || "-")}
                  </div>
                </div>
                <div style={styles.row}>
                  <div style={styles.label}>Created</div>
                  <div>{fmtDT(detail.registration.created_at)}</div>
                </div>
              </div>

              {/* Latest payment */}
              <div style={{ margin: "14px 0" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 6,
                  }}
                >
                  <div style={{ fontWeight: 700 }}>Latest Payment</div>
                  {detail.latest_payment && (
                    <button
                      type="button"
                      onClick={handleRefreshRazorpay}
                      disabled={refreshing}
                      style={{
                        border: "1px solid #0f766e",
                        background: "#ecfdf5",
                        color: "#0f766e",
                        borderRadius: 999,
                        padding: "4px 10px",
                        fontSize: 12,
                        cursor: "pointer",
                      }}
                    >
                      {refreshing
                        ? "Refreshing…"
                        : "Refresh from Razorpay (order)"}
                    </button>
                  )}
                </div>
                {refreshNote && (
                  <div
                    style={{
                      fontSize: 12,
                      marginBottom: 6,
                      color: "#15803d",
                    }}
                  >
                    {refreshNote}
                  </div>
                )}
                {!detail.latest_payment && (
                  <div style={{ fontSize: 13, color: "#6b7280" }}>
                    No payment captured yet.
                  </div>
                )}
                {detail.latest_payment && (
                  <div>
                    <div style={styles.row}>
                      <div style={styles.label}>Status</div>
                      <div>{StatusPill(detail.latest_payment.status)}</div>
                    </div>
                    <div style={styles.row}>
                      <div style={styles.label}>Amount (₹)</div>
                      <div>{detail.latest_payment.amount_rupees ?? "-"}</div>
                    </div>
                    <div style={styles.row}>
                      <div style={styles.label}>Method</div>
                      <div>{detail.latest_payment.method || "-"}</div>
                    </div>
                    <div style={styles.row}>
                      <div style={styles.label}>Captured At</div>
                      <div>{fmtDT(detail.latest_payment.captured_at)}</div>
                    </div>
                    <div style={styles.row}>
                      <div style={styles.label}>Order ID</div>
                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          alignItems: "center",
                        }}
                      >
                        <span style={{ wordBreak: "break-all" }}>
                          {detail.latest_payment.razorpay_order_id || "-"}
                        </span>
                        {detail.latest_payment.razorpay_order_id && (
                          <button
                            onClick={() =>
                              copy(detail.latest_payment.razorpay_order_id)
                            }
                            style={{
                              border: "1px solid #e5e7eb",
                              padding: "2px 6px",
                              borderRadius: 6,
                              background: "#fff",
                              cursor: "pointer",
                            }}
                          >
                            Copy
                          </button>
                        )}
                      </div>
                    </div>
                    <div style={styles.row}>
                      <div style={styles.label}>Payment ID</div>
                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          alignItems: "center",
                        }}
                      >
                        <span style={{ wordBreak: "break-all" }}>
                          {detail.latest_payment.razorpay_payment_id || "-"}
                        </span>
                        {detail.latest_payment.razorpay_payment_id && (
                          <button
                            onClick={() =>
                              copy(detail.latest_payment.razorpay_payment_id)
                            }
                            style={{
                              border: "1px solid #e5e7eb",
                              padding: "2px 6px",
                              borderRadius: 6,
                              background: "#fff",
                              cursor: "pointer",
                            }}
                          >
                            Copy
                          </button>
                        )}
                      </div>
                    </div>
                    <div style={styles.row}>
                      <div style={styles.label}>UPI RRN/UTR</div>
                      <div>{detail.latest_payment.upi_rrn_or_utr || "-"}</div>
                    </div>
                  </div>
                )}
              </div>

              {/* History */}
              {detail.history && detail.history.length > 0 && (
                <div style={{ marginTop: 10 }}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      borderBottom: "1px solid #e5e7eb",
                      paddingBottom: 4,
                      marginBottom: 4,
                    }}
                  >
                    Payment History
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                    }}
                  >
                    {detail.history.map((h) => (
                      <div
                        key={h.id}
                        style={{
                          padding: 8,
                          borderRadius: 8,
                          border: "1px solid #e5e7eb",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            fontSize: 12,
                          }}
                        >
                          <div style={{ fontWeight: 600 }}>
                            {fmtDT(h.created_at)}
                          </div>
                          <div>{pill(h.status)}</div>
                        </div>
                        <div
                          style={{
                            marginTop: 4,
                            fontSize: 13,
                            color: "#0f172a",
                          }}
                        >
                          {h.description || "-"}
                        </div>
                        <div
                          style={{
                            marginTop: 6,
                            fontSize: 13,
                            color: "#0f172a",
                          }}
                        >
                          {h.razorpay_payment_id ? (
                            <>
                              Payment: <code>{h.razorpay_payment_id}</code>
                              <br />
                            </>
                          ) : null}
                          {h.razorpay_order_id ? (
                            <>
                              Order: <code>{h.razorpay_order_id}</code>
                              <br />
                            </>
                          ) : null}
                          {h.upi_rrn_or_utr ? (
                            <>
                              UPI UTR/RRN: <code>{h.upi_rrn_or_utr}</code>
                              <br />
                            </>
                          ) : null}
                          {h.raw_note ? (
                            <>
                              Note: <code>{h.raw_note}</code>
                            </>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const nav = useNavigate();

  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const [data, setData] = useState({
    items: [],
    kpis: { total: 0, completed: 0, failed: 0, pending: 0 },
    total: 0,
  });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  // filters for Delegation Type & referrer
  const [courseFilter, setCourseFilter] = useState("");
  const [referrerFilter, setReferrerFilter] = useState("");

  // 🔹 today-only should be UNCHECKED initially
  const [onlyToday, setOnlyToday] = useState(false);

  const [courses, setCourses] = useState([]);
  const [referrers, setReferrers] = useState([]);

  // ✅ NEW: geo dropdown filters near export buttons
  const [stateFilter, setStateFilter] = useState("");
  const [districtFilter, setDistrictFilter] = useState("");
  const [talukFilter, setTalukFilter] = useState("");

  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [taluks, setTaluks] = useState([]);

  const [open, setOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const totalPages = Math.max(1, Math.ceil(data.total / size));

  const statuses = useMemo(
    () => [
      ["All", ""],
      ["Success", "COMPLETED"],
      ["Pending", "PENDING_PAYMENT"],
      ["Failed", "FAILED"],
    ],
    []
  );

  /** EXPORT HELPERS */
  const fetchAllForExport = async () => {
    const { data: res } = await api.get("/api/admin/registrations", {
      params: {
        q,
        status,
        course_id: courseFilter || undefined,
        referrer_id: referrerFilter || undefined,
        today: onlyToday ? 1 : undefined,

        // ✅ NEW geo filters
        state_id: stateFilter || undefined,
        district_id: districtFilter || undefined,
        taluk_id: talukFilter || undefined,

        page: 1,
        size: 10000,
      },
    });
    return res.items || [];
  };

  const handleExportExcel = async () => {
    try {
      const rows = await fetchAllForExport();
      if (!rows.length) return;

      const mapped = rows.map((r, idx) => ({
        "#": idx + 1,
        Date: fmtDate(r.created_at),
        Name: r.full_name,
        Mobile: r.mobile,
        Email: r.email,
        "Delegation Type": r.course_name,
        College: r.college_name || "",
        "Referred By": r.referrer_name || "",
        "Amount (₹)": r.amount_rupees,
        "Payment Status": r.payment_status,
        "Reg Status": r.registration_status,
        "Payment ID": r.razorpay_payment_id,
        "Order ID": r.razorpay_order_id,
        "UPI RRN/UTR": r.upi_rrn_or_utr,
        Method: r.method,
      }));

      const ws = XLSX.utils.json_to_sheet(mapped);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Registrations");
      XLSX.writeFile(wb, "yogapay_registrations.xlsx");
    } catch (e) {
      console.error("Export Excel failed", e);
      alert("Failed to export Excel");
    }
  };

  const handleExportPDF = async () => {
    try {
      const rows = await fetchAllForExport();
      if (!rows.length) return;

      const doc = new jsPDF("landscape", "pt", "A4");

      doc.setFontSize(14);
      doc.text("YogaPay – Registrations & Payments", 40, 24);

      const head = [
        "#",
        "Date",
        "Name",
        "Mobile",
        "Delegation Type",
        "College",
        "Referrer",
        "Amount (₹)",
        "Pay Status",
        "Reg Status",
        "Payment ID",
        "Order ID",
        "UPI RRN/UTR",
        "Method",
      ];

      const body = rows.map((r, idx) => [
        idx + 1,
        fmtDate(r.created_at),
        r.full_name,
        r.mobile,
        r.course_name,
        r.college_name || "",
        r.referrer_name || "",
        r.amount_rupees,
        r.payment_status,
        r.registration_status,
        r.razorpay_payment_id,
        r.razorpay_order_id,
        r.upi_rrn_or_utr,
        r.method,
      ]);

      autoTable(doc, {
        startY: 26,
        head: [head],
        body,
        styles: { fontSize: 8 },
        headStyles: {
          fillColor: [15, 23, 42],
          textColor: 255,
        },
      });

      doc.save("yogapay_registrations.pdf");
    } catch (e) {
      console.error("Export PDF failed", e);
      alert("Failed to export PDF");
    }
  };

  const fetchCoursesReferrers = async () => {
    try {
      const [cRes, rRes] = await Promise.all([
        api.get("/api/admin/courses"),
        api.get("/api/admin/referrers"),
      ]);
      setCourses(cRes.data || []);
      setReferrers(rRes.data || []);
    } catch (e) {
      console.error("Error loading courses/referrers", e);
    }
  };

  // ✅ NEW: geo masters for dropdowns
  const normalizeList = (payload) => {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload.items)) return payload.items;
    if (Array.isArray(payload.rows)) return payload.rows;
    return [];
  };

  const fetchStates = async () => {
  try {
    const { data } = await api.get("/api/admin/states");
    setStates(normalizeList(data));
  } catch (e) {
    console.error("Failed to load states", e);
    setStates([]);
  }
};

const fetchDistricts = async (stateId) => {
  if (!stateId) {
    setDistricts([]);
    return;
  }
  try {
    const { data } = await api.get("/api/admin/districts", {
      params: { state_id: stateId },
    });
    setDistricts(normalizeList(data));
  } catch (e) {
    console.error("Failed to load districts", e);
    setDistricts([]);
  }
};

const fetchTaluks = async (districtId) => {
  if (!districtId) {
    setTaluks([]);
    return;
  }
  try {
    const { data } = await api.get("/api/admin/taluks", {
      params: { district_id: districtId },
    });
    setTaluks(normalizeList(data));
  } catch (e) {
    console.error("Failed to load taluks", e);
    setTaluks([]);
  }
};

  // debounced search
  const [debouncedQ, setDebouncedQ] = useState(q);
  useEffect(() => {
    const h = setTimeout(() => setDebouncedQ(q), 500);
    return () => clearTimeout(h);
  }, [q]);

  const fetchData = async (opts = {}) => {
    try {
      setLoading(true);
      setErr("");
      const { data: res } = await api.get("/api/admin/registrations", {
        params: {
          q: debouncedQ || undefined,
          status: status || undefined,
          course_id: courseFilter || undefined,
          referrer_id: referrerFilter || undefined,
          today: onlyToday ? 1 : undefined,

          // ✅ NEW geo filters
          state_id: stateFilter || undefined,
          district_id: districtFilter || undefined,
          taluk_id: talukFilter || undefined,

          page,
          size,
          ...opts,
        },
      });
      setData(res);
      setLastUpdated(new Date());
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoursesReferrers();
    fetchStates(); // ✅ NEW
  }, []);

  // ✅ NEW: dependent loads
  useEffect(() => {
    // when state changes, reset district/taluk and load districts
    setDistrictFilter("");
    setTalukFilter("");
    setTaluks([]);
    fetchDistricts(stateFilter);
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateFilter]);

  useEffect(() => {
    // when district changes, reset taluk and load taluks
    setTalukFilter("");
    fetchTaluks(districtFilter);
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [districtFilter]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedQ,
    status,
    page,
    size,
    courseFilter,
    referrerFilter,
    onlyToday,
    stateFilter,
    districtFilter,
    talukFilter,
  ]);

  useEffect(() => {
    if (!autoRefresh) return;

    const id = setInterval(() => {
      fetchData();
    }, 30000); // 30 seconds

    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    autoRefresh,
    debouncedQ,
    status,
    page,
    size,
    courseFilter,
    referrerFilter,
    onlyToday,
    stateFilter,
    districtFilter,
    talukFilter,
  ]);

  const handleLogout = () => {
    logout();
    nav("/admin/login");
  };

  const onSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchData({ page: 1 });
  };

  const onRowClick = (id) => {
    setDetailId(id);
    setOpen(true);
  };

  const formattedLastUpdated = lastUpdated
    ? lastUpdated.toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })
    : "-";

  return (
    <div
      style={{
        background:
          "linear-gradient(180deg,#f3f4f6 0%,#e5e7eb 40%,#f9fafb 100%)",
        minHeight: "100vh",
      }}
    >
      {/* Top Bar */}
      <div
        style={{
          height: 56,
          background: "#ffffff",
          borderBottom: "1px solid #e5e7eb",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 20px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 12,
              background:
                "linear-gradient(135deg, #0f766e 0%, #22c55e 50%, #0ea5e9 100%)",
            }}
          />
          <div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>YogaPay Admin</div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              Registrations & Razorpay Payments
            </div>
          </div>
        </div>

        {/* 🔔 Bell + Logout */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <NotificationBell />
          <button
            onClick={handleLogout}
            style={{
              border: "1px solid #e5e7eb",
              background: "#fff",
              padding: "6px 10px",
              borderRadius: 8,
              cursor: "pointer",
              fontSize: 14,
            }}
          >
            Logout
          </button>
        </div>
      </div>

      <main
        style={{
          maxWidth: 1100,
          margin: "0 auto",
          padding: "12px 16px 16px",
        }}
      >
        <div
          style={{
            margin: "6px 0 12px 0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div>
            <div style={{ fontSize: 24, fontWeight: 700 }}>
              YogaPay Dashboard
            </div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              Subscription registrations and Razorpay payment status in one view
            </div>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 4,
              fontSize: 12,
            }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              <span style={{ color: "#4b5563" }}>Auto refresh (30s)</span>
            </label>
            <span style={{ color: "#9ca3af" }}>
              Last updated: <strong>{formattedLastUpdated}</strong>
            </span>
          </div>
        </div>

        {/* Quick admin links */}
        <div
          style={{
            display: "flex",
            gap: 8,
            marginBottom: 16,
            flexWrap: "wrap",
          }}
        >
          <Link
            to="/admin/referrers"
            style={{
              textDecoration: "none",
              padding: "10px 18px",
              borderRadius: 999,
              border: "1px solid #059669",
              color: "#065f46",
              fontSize: 14,
              fontWeight: 600,
              background: "#dcfce7",
            }}
          >
            Manage Referrers
          </Link>
          <Link
            to="/admin/courses"
            style={{
              textDecoration: "none",
              padding: "10px 18px",
              borderRadius: 999,
              border: "1px solid #2563eb",
              color: "#1d4ed8",
              fontSize: 14,
              fontWeight: 600,
              background: "#dbeafe",
            }}
          >
            Manage Delegation Types
          </Link>
          <Link
            to="/admin/settings"
            style={{
              textDecoration: "none",
              padding: "10px 18px",
              borderRadius: 999,
              border: "1px solid #6b7280",
              color: "#111827",
              fontSize: 14,
              fontWeight: 600,
              background: "#e5e7eb",
            }}
          >
            Site Settings
          </Link>
          <Link
            to="/admin/carousel"
            style={{
              textDecoration: "none",
              padding: "10px 18px",
              borderRadius: 999,
              border: "1px solid #ec4899",
              color: "#be185d",
              fontSize: 14,
              fontWeight: 600,
              background: "#fdf2f8",
            }}
          >
            Home Carousel
          </Link>
          <Link
            to="/admin/events"
            style={{
              textDecoration: "none",
              padding: "10px 18px",
              borderRadius: 999,
              border: "1px solid #0ea5e9",
              color: "#075985",
              fontSize: 14,
              fontWeight: 600,
              background: "#e0f2fe",
            }}
          >
            Events
          </Link>
          <Link
            to="/admin/feedback"
            style={{
              textDecoration: "none",
              padding: "10px 18px",
              borderRadius: 999,
              border: "1px solid #8b5cf6",
              color: "#4c1d95",
              fontSize: 14,
              fontWeight: 600,
              background: "#eee5ff",
            }}
          >
            Feedback
          </Link>
          <Link
            to="/admin/notifications"
            style={{
              textDecoration: "none",
              padding: "10px 18px",
              borderRadius: 999,
              border: "1px solid #0f172a",
              color: "#0f172a",
              fontSize: 14,
              fontWeight: 600,
              background: "#f9fafb",
            }}
          >
            Push Notification
          </Link>
          <Link
            to="/admin/colleges"
            style={{
              textDecoration: "none",
              padding: "10px 18px",
              borderRadius: 999,
              border: "1px solid #2563eb",
              color: "#1d4ed8",
              fontSize: 14,
              fontWeight: 600,
              background: "#dbeafe",
            }}
          >
            Manage Colleges
          </Link>

          <Link
            to="/admin/states"
            style={{
              textDecoration: "none",
              padding: "10px 18px",
              borderRadius: 999,
              border: "1px solid #eb2592ff",
              color: "#d81d77ff",
              fontSize: 14,
              fontWeight: 600,
              background: "#dbeafe",
            }}
          >
            Manage States
          </Link>
          <Link
            to="/admin/districts"
            style={{
              textDecoration: "none",
              padding: "10px 18px",
              borderRadius: 999,
              border: "1px solid #2563eb",
              color: "#1d4ed8",
              fontSize: 14,
              fontWeight: 600,
              background: "#dbeafe",
            }}
          >
            Manage Districts
          </Link>

          <Link
            to="/admin/taluks"
            style={{
              textDecoration: "none",
              padding: "10px 18px",
              borderRadius: 999,
              border: "1px solid #3feb25ff",
              color: "#1d4ed8",
              fontSize: 14,
              fontWeight: 600,
              background: "#8d949dff",
            }}
          >
            Manage Taluks
          </Link>

          <Link
            to="/admin/receipt-titles"
            style={{
              textDecoration: "none",
              padding: "10px 18px",
              borderRadius: 999,
              border: "1px solid #eb2525ff",
              color: "#1dd833ff",
              fontSize: 14,
              fontWeight: 600,
              background: "#8d949dff",
            }}
          >
            Manage Titles
          </Link>
          <Link
            to="/admin/aboutus"
            style={{
              textDecoration: "none",
              padding: "10px 18px",
              borderRadius: 999,
              border: "1px solid #eb2525ff",
              color: "#d4e3d6ff",
              fontSize: 14,
              fontWeight: 600,
              background: "#8d949dff",
            }}
          >
            Manage About Event
          </Link>

          <Link
            to="/admin/receipt-signatures"
            style={{
              textDecoration: "none",
              padding: "10px 18px",
              borderRadius: 999,
              border: "1px solid #eb259fff",
              color: "#1e5d42ff",
              fontSize: 14,
              fontWeight: 600,
              background: "#8d949dff",
            }}
          >
            Manage Signature
          </Link>
        </div>

        {/* KPI cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))",
            gap: 10,
            marginBottom: 10,
          }}
        >
          {[
            ["Total", data.kpis.total],
            ["Success", data.kpis.completed],
            ["Failed", data.kpis.failed],
            ["Pending", data.kpis.pending],
          ].map(([label, val]) => (
            <div
              key={label}
              style={{
                background: "#fff",
                borderRadius: 14,
                border: "1px solid #e5e7eb",
                padding: 10,
                minHeight: 56,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                boxShadow: "0 4px 12px rgba(15,23,42,0.03)",
              }}
            >
              <div style={{ fontSize: 11, color: "#64748b" }}>{label}</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{val}</div>
            </div>
          ))}
        </div>

        {/* Filters row (UNCHANGED) */}
        <form
          onSubmit={onSearch}
          style={{
            display: "flex",
            gap: 8,
            marginBottom: 10,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <input
            type="text"
            placeholder="Search name/mobile/email..."
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            style={{
              flex: 1,
              minWidth: 200,
              padding: "8px 10px",
              borderRadius: 999,
              border: "1px solid #d1d5db",
              fontSize: 14,
            }}
          />

          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
            style={{
              padding: "8px 10px",
              borderRadius: 999,
              border: "1px solid #d1d5db",
              fontSize: 14,
              minWidth: 120,
            }}
          >
            {statuses.map(([label, value]) => (
              <option key={value || "ALL"} value={value}>
                {label}
              </option>
            ))}
          </select>

          <select
            value={courseFilter}
            onChange={(e) => {
              setCourseFilter(e.target.value);
              setPage(1);
            }}
            style={{
              padding: "8px 10px",
              borderRadius: 999,
              border: "1px solid #d1d5db",
              fontSize: 14,
              minWidth: 160,
            }}
          >
            <option value="">All Delegation Types</option>
            {courses.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>

          <select
            value={referrerFilter}
            onChange={(e) => {
              setReferrerFilter(e.target.value);
              setPage(1);
            }}
            style={{
              padding: "8px 10px",
              borderRadius: 999,
              border: "1px solid #d1d5db",
              fontSize: 14,
              minWidth: 140,
            }}
          >
            <option value="">All Referrers</option>
            {referrers.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>

          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              fontSize: 13,
              color: "#4b5563",
              whiteSpace: "nowrap",
            }}
          >
            <input
              type="checkbox"
              checked={onlyToday}
              onChange={(e) => {
                setOnlyToday(e.target.checked);
                setPage(1);
              }}
            />
            Today only
          </label>

          <button
            type="submit"
            style={{
              padding: "8px 16px",
              borderRadius: 999,
              border: "none",
              background: "#047857",
              color: "#fff",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Search
          </button>
        </form>

        {/* Export buttons + ✅ NEW geo filters beside them */}
        <div
          style={{
            display: "flex",
            gap: 10,
            marginBottom: 10,
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={handleExportExcel}
              style={{
                padding: "6px 12px",
                borderRadius: 999,
                border: "1px solid #0f766e",
                background: "#ecfdf5",
                color: "#065f46",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Export Excel
            </button>
            <button
              type="button"
              onClick={handleExportPDF}
              style={{
                padding: "6px 12px",
                borderRadius: 999,
                border: "1px solid #7c2d12",
                background: "#fef3c7",
                color: "#7c2d12",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Export PDF
            </button>
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <select
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
              style={{
                padding: "6px 10px",
                borderRadius: 999,
                border: "1px solid #d1d5db",
                fontSize: 13,
                minWidth: 160,
                background: "#fff",
              }}
              title="Filter by State"
            >
              <option value="">All States</option>
              {states.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>

            <select
              value={districtFilter}
              onChange={(e) => setDistrictFilter(e.target.value)}
              disabled={!stateFilter}
              style={{
                padding: "6px 10px",
                borderRadius: 999,
                border: "1px solid #d1d5db",
                fontSize: 13,
                minWidth: 170,
                background: !stateFilter ? "#f3f4f6" : "#fff",
                cursor: !stateFilter ? "not-allowed" : "pointer",
              }}
              title="Filter by District"
            >
              <option value="">All Districts</option>
              {districts.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>

            <select
              value={talukFilter}
              onChange={(e) => setTalukFilter(e.target.value)}
              disabled={!districtFilter}
              style={{
                padding: "6px 10px",
                borderRadius: 999,
                border: "1px solid #d1d5db",
                fontSize: 13,
                minWidth: 160,
                background: !districtFilter ? "#f3f4f6" : "#fff",
                cursor: !districtFilter ? "not-allowed" : "pointer",
              }}
              title="Filter by Taluk"
            >
              <option value="">All Taluks</option>
              {taluks.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Table */}
        <div
          style={{
            background: "#ffffff",
            borderRadius: 16,
            border: "1px solid #e5e7eb",
            overflow: "hidden",
          }}
        >
          {loading ? (
            <div style={{ padding: 16 }}>Loading…</div>
          ) : err ? (
            <div style={{ padding: 16, color: "#b91c1c" }}>{err}</div>
          ) : data.items.length === 0 ? (
            <div style={{ padding: 16 }}>No registrations found.</div>
          ) : (
            <div style={{ width: "100%", overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  minWidth: 900,
                  borderCollapse: "collapse",
                  fontSize: 13,
                }}
              >
                <thead>
                  <tr style={{ background: "#f9fafb" }}>
                    <th style={{ padding: 8, borderBottom: "1px solid #e5e7eb" }}>
                      ID
                    </th>
                    <th style={{ padding: 8, borderBottom: "1px solid #e5e7eb" }}>
                      Name
                    </th>
                    <th style={{ padding: 8, borderBottom: "1px solid #e5e7eb" }}>
                      College
                    </th>
                    <th style={{ padding: 8, borderBottom: "1px solid #e5e7eb" }}>
                      Mobile
                    </th>
                    <th style={{ padding: 8, borderBottom: "1px solid #e5e7eb" }}>
                      Delegation Type
                    </th>
                    <th style={{ padding: 8, borderBottom: "1px solid #e5e7eb" }}>
                      Referred By
                    </th>
                    <th style={{ padding: 8, borderBottom: "1px solid #e5e7eb" }}>
                      Amount (₹)
                    </th>
                    <th style={{ padding: 8, borderBottom: "1px solid #e5e7eb" }}>
                      Payment
                    </th>
                    <th style={{ padding: 8, borderBottom: "1px solid #e5e7eb" }}>
                      Reg Status
                    </th>
                    <th style={{ padding: 8, borderBottom: "1px solid #e5e7eb" }}>
                      Method
                    </th>
                    <th style={{ padding: 8, borderBottom: "1px solid #e5e7eb" }}>
                      Created
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((r) => (
                    <tr
                      key={r.id}
                      style={{
                        cursor: "pointer",
                        background: "#ffffff",
                      }}
                      onClick={() => onRowClick(r.id)}
                    >
                      <td
                        style={{
                          padding: 8,
                          borderTop: "1px solid #f1f5f9",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {r.id}
                      </td>
                      <td
                        style={{
                          padding: 8,
                          borderTop: "1px solid #f1f5f9",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {r.full_name}
                      </td>
                      <td
                        style={{
                          padding: 8,
                          borderTop: "1px solid #f1f5f9",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {r.college_name || "-"}
                      </td>
                      <td
                        style={{
                          padding: 8,
                          borderTop: "1px solid #f1f5f9",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {r.mobile}
                      </td>
                      <td style={{ padding: 8, borderTop: "1px solid #f1f5f9" }}>
                        {r.course_name}
                      </td>
                      <td style={{ padding: 8, borderTop: "1px solid #f1f5f9" }}>
                        {r.referrer_name || "-"}
                      </td>
                      <td
                        style={{
                          padding: 8,
                          borderTop: "1px solid #f1f5f9",
                          textAlign: "right",
                        }}
                      >
                        {r.amount_rupees ?? "-"}
                      </td>
                      <td style={{ padding: 8, borderTop: "1px solid #f1f5f9" }}>
                        {pill(r.payment_status)}
                      </td>
                      <td style={{ padding: 8, borderTop: "1px solid #f1f5f9" }}>
                        {pill(r.registration_status)}
                      </td>
                      <td style={{ padding: 8, borderTop: "1px solid #f1f5f9" }}>
                        {r.method || "-"}
                      </td>
                      <td
                        style={{
                          padding: 8,
                          borderTop: "1px solid #f1f5f9",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {fmtDT(r.created_at)}
                      </td>
                    </tr>
                  ))}

                  {/* Pagination row */}
                  <tr>
                    <td
                      colSpan={11}
                      style={{
                        padding: 8,
                        borderTop: "1px solid #e5e7eb",
                        background: "#f9fafb",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          fontSize: 12,
                          flexWrap: "wrap",
                          gap: 8,
                        }}
                      >
                        <div>
                          Showing{" "}
                          <strong>
                            {data.items.length} of {data.total}
                          </strong>{" "}
                          registrations
                        </div>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <span style={{ marginRight: 4 }}>Rows per page:</span>
                          <select
                            value={size}
                            onChange={(e) => {
                              setSize(Number(e.target.value));
                              setPage(1);
                            }}
                            style={{
                              borderRadius: 6,
                              border: "1px solid #e5e7eb",
                              padding: "4px 6px",
                            }}
                          >
                            {[10, 20, 50, 100].map((s) => (
                              <option key={s} value={s}>
                                {s}/page
                              </option>
                            ))}
                          </select>
                          <div style={{ marginLeft: 12 }}>
                            Page {page} of {totalPages}
                          </div>
                          <div
                            style={{
                              marginLeft: 8,
                              display: "flex",
                              gap: 4,
                            }}
                          >
                            <button
                              disabled={page <= 1}
                              onClick={() => setPage(1)}
                              style={{
                                padding: "3px 6px",
                                borderRadius: 6,
                                border: "1px solid #e5e7eb",
                                background: "#fff",
                                cursor: page <= 1 ? "not-allowed" : "pointer",
                              }}
                            >
                              ⏮
                            </button>
                            <button
                              disabled={page <= 1}
                              onClick={() => setPage((p) => Math.max(1, p - 1))}
                              style={{
                                padding: "3px 6px",
                                borderRadius: 6,
                                border: "1px solid #e5e7eb",
                                background: "#fff",
                                cursor: page <= 1 ? "not-allowed" : "pointer",
                              }}
                            >
                              ◀
                            </button>
                            <button
                              disabled={page >= totalPages}
                              onClick={() =>
                                setPage((p) => Math.min(totalPages, p + 1))
                              }
                              style={{
                                padding: "3px 6px",
                                borderRadius: 6,
                                border: "1px solid #e5e7eb",
                                background: "#fff",
                                cursor:
                                  page >= totalPages ? "not-allowed" : "pointer",
                              }}
                            >
                              ▶
                            </button>
                            <button
                              disabled={page >= totalPages}
                              onClick={() => setPage(totalPages)}
                              style={{
                                padding: "3px 6px",
                                borderRadius: 6,
                                border: "1px solid #e5e7eb",
                                background: "#fff",
                                cursor:
                                  page >= totalPages ? "not-allowed" : "pointer",
                              }}
                            >
                              ⏭
                            </button>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Right-side details drawer */}
      <DetailsDrawer open={open} id={detailId} onClose={() => setOpen(false)} />
    </div>
  );
}
